import {
  LedgerEntryType,
  TimelineItemType,
  TimelinePaymentEvent,
  TimelinePaymentOrder,
  TreatmentPaymentLedger,
  TreatmentPaymentOrder,
} from '@/components/Timeline/types';
import { sumRefunded } from '@/services/payments';

/** Money arrives as float dollars; render cents, not floating-point noise. */
function roundToCents(amount: number): number {
  return Math.round(amount * 100) / 100;
}

/** No FHIR resource exists for money, so payment items carry no reference. */
const NO_FHIR_REFERENCE = {};

/**
 * Builds the billing timeline cards for one patient.
 *
 * - **Card 01** (`PAYMENT_ORDER`) — stateful; aggregates, never records. Every
 *   figure on it derives from the order or the ledger, so nothing is true only
 *   there. Stamped with the order's real `createdAt`.
 * - **Card 02** (`PAYMENT_EVENT`, `kind: 'paid'`) — one per payment charge,
 *   stamped with the charge's own time.
 * - **Card 03** (`PAYMENT_EVENT`, `kind: 'paid-in-full'`) — derived, once per
 *   order, dated by the payment that closed it.
 *
 * There is deliberately no refund card. A Stripe refund is a cumulative
 * `amountRefunded` on the original charge rather than an event, so it has no date
 * of its own; dating a card from the original payment would put a wrong date on a
 * financial surface. The refunded total goes on card 01 instead.
 *
 * `now` is unused for stamping and kept for the caller's convenience — every
 * timestamp here comes from the data. Method-state ageing, which does need the
 * clock, lives in `describeMethodState`.
 */
export function mapPaymentOrderToTimelineItems(
  order: TreatmentPaymentOrder | null,
  ledger: TreatmentPaymentLedger,
  _now: Date,
  /**
   * Charge id -> the teammate who keyed it, from `Provenance`. Absent for a
   * webhook-written charge; that absence is what marks a card as system-written,
   * rather than any inference from the processor.
   */
  recordedBy: ReadonlyMap<string, string> = new Map(),
  /**
   * Who created the order, from its `Provenance` — the win attribution
   * (BAR-2614). Undefined for an order that predates it or that the legacy
   * Close path built; the card renders that as unattributed rather than
   * guessing at a name.
   */
  createdBy?: string
): (TimelinePaymentOrder | TimelinePaymentEvent)[] {
  if (!order) {
    return [];
  }

  const payments = ledger.entries.filter((entry) => entry.type === LedgerEntryType.PAYMENT && entry.amount > 0);

  const items: (TimelinePaymentOrder | TimelinePaymentEvent)[] = [
    {
      reference: NO_FHIR_REFERENCE,
      type: TimelineItemType.PAYMENT_ORDER,
      id: order.checkoutSessionId,
      order: {
        ...order,
        amountTotal: roundToCents(order.amountTotal),
        amountPaid: roundToCents(order.amountPaid),
        balance: roundToCents(order.balance),
      },
      timestamp: new Date(order.createdAt),
      createdBy,
      amountRefunded: sumRefunded(ledger.entries),
      ledgerTruncated: ledger.truncated,
      ledgerPartial: ledger.partial,
      // The latest payment against each leg. A leg can carry more than one
      // charge, and the row says when it was settled, not when it was first
      // touched.
      legSettledAt: payments.reduce<Record<string, string>>((byLeg, payment) => {
        const current = byLeg[payment.paymentIntentId];
        if (!current || payment.createdAt > current) {
          byLeg[payment.paymentIntentId] = payment.createdAt;
        }
        return byLeg;
      }, {}),
    },
  ];

  for (const payment of payments) {
    items.push({
      reference: NO_FHIR_REFERENCE,
      type: TimelineItemType.PAYMENT_EVENT,
      id: payment.chargeId,
      kind: 'paid',
      amount: roundToCents(payment.amount),
      processor: payment.processor,
      processorRefId: payment.processorRefId,
      payerName: payment.payerName,
      recordedBy: recordedBy.get(payment.chargeId),
      timestamp: new Date(payment.createdAt),
    });
  }

  // A zero balance with nothing paid is a free order, not a settlement worth
  // announcing. An overpayment is still paid in full.
  if (order.balance <= 0 && order.amountPaid > 0) {
    const closingPayment = payments.reduce<(typeof payments)[number] | undefined>(
      (latest, payment) => (!latest || payment.createdAt > latest.createdAt ? payment : latest),
      undefined
    );

    items.push({
      reference: NO_FHIR_REFERENCE,
      type: TimelineItemType.PAYMENT_EVENT,
      id: `${order.checkoutSessionId}-paid-in-full`,
      kind: 'paid-in-full',
      amount: roundToCents(order.amountPaid),
      serviceName: order.items.find((item) => item.serviceName)?.serviceName ?? undefined,
      // Dated by the payment that closed it, falling back to the order itself
      // when the ledger could not be read.
      timestamp: new Date(closingPayment?.createdAt ?? order.createdAt),
    });
  }

  return items;
}
